/**
 * Reset Password Page - Set new password using token from email
 * Enforces strong password policy with real-time feedback
 * After success: auto-login and redirect to dashboard
 */
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Lock, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, X } from "lucide-react";

const PasswordRule = ({ met, label }) => (
  <div className="flex items-center gap-2 text-xs">
    {met ? (
      <CheckCircle className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
    ) : (
      <X className="h-3.5 w-3.5 text-slate-500 shrink-0" />
    )}
    <span className={met ? "text-emerald-400" : "text-slate-500"}>{label}</span>
  </div>
);

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { login } = useAuth();

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [fullEmail, setFullEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Password strength rules
  const rules = [
    { key: "len", label: "At least 8 characters", test: (p) => p.length >= 8 },
    { key: "upper", label: "1 uppercase letter (A-Z)", test: (p) => /[A-Z]/.test(p) },
    { key: "num", label: "1 number (0-9)", test: (p) => /[0-9]/.test(p) },
    { key: "special", label: "1 special character (!@#$%...)", test: (p) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(p) },
  ];
  const allRulesMet = rules.every((r) => r.test(password));

  useEffect(() => { validateToken(); }, [token]);

  const validateToken = async () => {
    if (!token) { setTokenValid(false); setErrorMessage("No reset token provided"); setValidating(false); return; }
    try {
      const response = await api.get(`/password-reset/validate-token?token=${token}`);
      if (response.data.valid) {
        setTokenValid(true);
        setMaskedEmail(response.data.email);
        // Store full email if available (we will get it from the token response)
        if (response.data.full_email) {
          setFullEmail(response.data.full_email);
        }
      }
      else { setTokenValid(false); setErrorMessage(response.data.message || "Invalid or expired reset link"); }
    } catch { setTokenValid(false); setErrorMessage("Failed to validate reset link"); }
    finally { setValidating(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allRulesMet) { toast.error("Password does not meet all requirements"); return; }
    if (password !== confirmPassword) { toast.error("Passwords do not match"); return; }

    setLoading(true);
    try {
      const response = await api.post("/password-reset/verify-token", { token, new_password: password });
      if (response.data.success) {
        setSuccess(true);
        toast.success("Password updated successfully!");
        
        // Auto-login with the new password
        if (response.data.email) {
          setRedirecting(true);
          try {
            await login(response.data.email, password);
            toast.success("Welcome back! Redirecting to your dashboard...");
            setTimeout(() => navigate("/dashboard"), 1500);
          } catch (loginError) {
            // If auto-login fails, just redirect to login page
            console.error("Auto-login failed:", loginError);
            setTimeout(() => navigate("/login"), 2000);
          }
        } else {
          // No email returned, redirect to login
          setTimeout(() => navigate("/login"), 2000);
        }
      }
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to reset password"); }
    finally { setLoading(false); }
  };

  if (validating) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="text-center"><Loader2 className="h-8 w-8 animate-spin text-emerald-400 mx-auto mb-4" /><p className="text-slate-400">Validating reset link...</p></div>
    </div>
  );

  if (!tokenValid) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-900 rounded-2xl p-8 border border-slate-800 text-center">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><AlertCircle className="h-8 w-8 text-red-400" /></div>
        <h1 className="text-2xl font-bold text-white mb-2">Invalid Reset Link</h1>
        <p className="text-slate-400 mb-6">{errorMessage}</p>
        <div className="space-y-3">
          <Button onClick={() => navigate("/forgot-password")} className="w-full bg-emerald-600 hover:bg-emerald-700">Request New Reset Link</Button>
          <Button variant="outline" onClick={() => navigate("/login")} className="w-full border-slate-700 text-slate-300">Back to Login</Button>
        </div>
      </div>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-900 rounded-2xl p-8 border border-slate-800 text-center">
        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><CheckCircle className="h-8 w-8 text-emerald-400" /></div>
        <h1 className="text-2xl font-bold text-white mb-2">Password Updated!</h1>
        <p className="text-slate-400 mb-6">
          {redirecting 
            ? "Your password has been reset. Logging you in and redirecting to your dashboard..."
            : "Your password has been successfully reset."
          }
        </p>
        {redirecting ? (
          <div className="flex items-center justify-center gap-2 text-emerald-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Redirecting to dashboard...</span>
          </div>
        ) : (
          <Button onClick={() => navigate("/login")} className="w-full bg-emerald-600 hover:bg-emerald-700">Go to Login</Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" onClick={() => navigate("/login")} className="p-2 text-slate-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></Button>
            <div>
              <h1 className="text-2xl font-bold text-white">Create New Password</h1>
              <p className="text-sm text-slate-400">For account: {maskedEmail}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter new password" className="w-full pl-10 pr-10 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="new-password-input" required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">{showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}</button>
              </div>
              {/* Password strength rules */}
              {password.length > 0 && (
                <div className="mt-3 p-3 bg-slate-800/50 rounded-lg space-y-1.5">
                  {rules.map((r) => <PasswordRule key={r.key} met={r.test(password)} label={r.label} />)}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <input type={showPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-testid="confirm-password-input" required />
              </div>
              {confirmPassword && password !== confirmPassword && <p className="text-xs text-red-400 mt-1">Passwords do not match</p>}
            </div>

            <Button type="submit" disabled={loading || !allRulesMet || password !== confirmPassword} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 disabled:opacity-50" data-testid="reset-submit-btn">
              {loading ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" />Updating...</>) : (<><Lock className="h-5 w-5 mr-2" />Reset Password</>)}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-400 mt-6">Remember your password?{" "}<Link to="/login" className="text-emerald-400 hover:text-emerald-300">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
